import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Button, Dropdown, Avatar, Drawer, Typography, message } from 'antd';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout, toggleTheme } from 'sama-shared';
import {
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
  DownOutlined,
  SunOutlined,
  MoonOutlined,
} from '@ant-design/icons';
import { ExchangeRateWidget } from '../../../components/ExchangeRateWidget';
import { useBancaribeBCVSyncMutation } from '../api/index.api';
import { SidebarContent } from './SidebarContent';
import { DisplayCurrencyToggle } from './DisplayCurrencyToggle';
import { useTheme } from '../../../tokens';

const { Header, Content, Sider } = Layout;

export const AdminLayout: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, mode } = useTheme();

  const [bancaribeBCVSync, { isLoading: isSyncing }] = useBancaribeBCVSyncMutation();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getSelectedMenuKey = useCallback(() => {
    if (location.pathname.includes('/admin/partner-invoices')) return '13';
    if (location.pathname.includes('/admin/industries')) return '14';
    if (location.pathname.includes('/admin/payment-methods')) return '16';
    if (location.pathname.includes('/admin/partner-settlements')) return '10';
    if (location.pathname.includes('/admin/financial-report')) return '6';
    if (location.pathname.includes('/admin/settings')) return '5';
    if (location.pathname.includes('/admin/order-payments')) return '4';
    if (location.pathname.includes('/admin/delivery-financial-report')) return '12';
    if (location.pathname.includes('/admin/delivery-providers')) return '3';
    if (location.pathname.includes('/admin/partners')) return '2';
    if (location.pathname.includes('/admin/subscription-plans')) return '8';
    if (location.pathname.includes('/admin/subscriptions')) return '9';
    if (location.pathname.includes('/admin/invoices')) return '15';
    if (location.pathname.includes('/admin/orders')) return '11';
    return '1';
  }, [location.pathname]);

  const handleMenuClick = useCallback(
    ({ key }: { key: string }) => {
      setDrawerVisible(false);
      const routes: Record<string, string> = {
        '1': '/admin',
        '2': '/admin/partners',
        '3': '/admin/delivery-providers',
        '4': '/admin/order-payments',
        '5': '/admin/settings',
        '6': '/admin/financial-report',
        '8': '/admin/subscription-plans',
        '9': '/admin/subscriptions',
        '10': '/admin/partner-settlements',
        '11': '/admin/orders',
        '12': '/admin/delivery-financial-report',
        '13': '/admin/partner-invoices',
        '14': '/admin/industries',
        '15': '/admin/invoices',
        '16': '/admin/payment-methods',
      };
      if (routes[key]) navigate(routes[key]);
    },
    [navigate]
  );

  const handleLogout = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const handleBancaribeSync = useCallback(async () => {
    try {
      await bancaribeBCVSync().unwrap();
      message.success('Tasa BCV sincronizada correctamente');
    } catch (err: any) {
      message.error(err?.data?.message || 'No se pudo sincronizar la tasa BCV');
    }
  }, [bancaribeBCVSync]);

  const profileDropdownItems = {
    items: [
      {
        key: 'logout',
        label: 'Cerrar sesión',
        icon: <LogoutOutlined />,
        danger: true,
        onClick: handleLogout,
      },
    ],
  };

  return (
    <Layout style={{ minHeight: '100vh', background: t.base }}>
      {/* Mobile Drawer */}
      <Drawer
        placement="left"
        closable={false}
        onClose={() => setDrawerVisible(false)}
        open={isMobile && drawerVisible}
        width={t.sidebarWidth}
        styles={{ body: { padding: 0, background: t.base } }}
      >
        <SidebarContent
          selectedKeys={[getSelectedMenuKey()]}
          onMenuClick={handleMenuClick}
          onLogout={handleLogout}
        />
      </Drawer>

      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          width={t.sidebarWidth}
          style={{
            background: t.base,
            borderRight: `1px solid ${t.border}`,
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
          }}
          theme="dark"
        >
          <SidebarContent
            selectedKeys={[getSelectedMenuKey()]}
            onMenuClick={handleMenuClick}
            onLogout={handleLogout}
          />
        </Sider>
      )}

      {/* Main Content Area */}
      <Layout
        style={{
          background: t.base,
          marginLeft: isMobile ? 0 : t.sidebarWidth,
          minHeight: '100vh',
        }}
      >
        {/* Header */}
        <Header
          style={{
            background: t.surface,
            borderBottom: `1px solid ${t.border}`,
            padding: `0 ${isMobile ? t.contentPaddingMobile : t.contentPadding}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 99,
            height: t.headerHeight,
            lineHeight: `${t.headerHeight}px`,
            transition: 'background-color 200ms ease, border-color 200ms ease',
          }}
        >
          {/* Left: Mobile menu + Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerVisible(true)}
                style={{
                  color: t.textPrimary,
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            )}
            <Typography.Text
              style={{
                color: t.textSecondary,
                fontSize: t.fontLabel,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {isMobile ? 'SAMA Admin' : 'Consola de Control de Plataforma'}
            </Typography.Text>
          </div>

          {/* Right: Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExchangeRateWidget onSync={handleBancaribeSync} isSyncing={isSyncing} />
            {/* Pantallas cuyos montos responden al widget: aliados, pagos, comisiones, facturas */}
            {['2', '4', '10', '13'].includes(getSelectedMenuKey()) && (
              <DisplayCurrencyToggle compact={isMobile} />
            )}
            <Button
              type="text"
              icon={mode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
              onClick={() => dispatch(toggleTheme())}
              style={{
                color: t.textSecondary,
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: t.radiusSm,
                transition: 'all 150ms ease',
              }}
            />
            <Dropdown menu={profileDropdownItems} trigger={['click']}>
              <Button
                type="text"
                style={{
                  color: t.textPrimary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  height: 36,
                  padding: '4px 8px',
                  borderRadius: t.radiusSm,
                  transition: 'all 150ms ease',
                }}
              >
                <Avatar size={28} icon={<UserOutlined />} style={{ backgroundColor: t.primary }} />
                {!isMobile && (
                  <span style={{ fontWeight: 500, fontSize: t.fontLabel }}>Super Admin</span>
                )}
                <DownOutlined style={{ fontSize: 10, color: t.textMuted }} />
              </Button>
            </Dropdown>
          </div>
        </Header>

        {/* Content */}
        <Content
          style={{
            padding: isMobile ? t.contentPaddingMobile : t.contentPadding,
            minHeight: `calc(100vh - ${t.headerHeight}px)`,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
